"use client";
import { signOut } from "../../session/session-actions";
import type { PostHog } from "posthog-js";
import { redactUrlProperties } from "./redact-analytics-properties";
import type { AnalyticsEventName } from "./events";
import {
  posthogBrowserApiHost,
  posthogProjectKey,
  posthogUiHost,
} from "../config";

// Hosts start analytics on idle. Queue operations in order so identity changes
// and clicks keep their relationship while the SDK loads.
let client: PostHog | null = null;
const queued: ((client: PostHog) => void)[] = [];

function withAnalytics(call: (client: PostHog) => void): void {
  if (client) {
    call(client);
    return;
  }

  queued.push(call);
}

export const deferredAnalytics: Pick<
  PostHog,
  "capture" | "identify" | "reset"
> = {
  capture: (...args) => {
    withAnalytics((analytics) => analytics.capture(...args));
    return undefined;
  },
  identify: (...args) => {
    withAnalytics((analytics) => analytics.identify(...args));
    return undefined;
  },
  reset: (...args) => withAnalytics((analytics) => analytics.reset(...args)),
};
let starting: Promise<void> | null = null;

export function startAnalytics(): Promise<void> {
  starting ??= loadAndInitAnalytics();
  return starting;
}

async function loadAndInitAnalytics(): Promise<void> {
  const { default: posthog } = await import("posthog-js");
  posthog.init(posthogProjectKey, {
    // The Observatory backend is the only flag evaluator (spec 0089). posthog-js carries
    // the values it decided as $feature/<key> super properties and never fetches its own.
    advanced_disable_feature_flags: true,
    api_host: posthogBrowserApiHost,
    capture_pageview: "history_change",
    capture_pageleave: true,
    defaults: "2026-01-30",
    opt_in_site_apps: true,
    person_profiles: "identified_only",
    ui_host: posthogUiHost,
    before_send(event) {
      for (const properties of [
        event.properties,
        event.$set,
        event.$set_once,
      ]) {
        if (properties) redactUrlProperties(properties);
      }

      return event;
    },
    loaded(loadedClient) {
      if (process.env.NODE_ENV === "development") {
        loadedClient.debug();
      }
    },
  });
  client = posthog;

  for (const call of queued) {
    call(posthog);
  }

  queued.length = 0;
}

export function captureAnalyticsEvent({
  event,
  properties,
}: {
  readonly event: AnalyticsEventName;
  readonly properties?:
    | Record<string, string | number | boolean | null | undefined>
    | undefined;
}): void {
  const timestamp = new Date();
  deferredAnalytics.capture(event, properties, { timestamp });
}

export function identifyAnalyticsUser({
  userId,
  email,
}: {
  readonly userId: string;
  readonly email?: (string | null) | undefined;
}): void {
  deferredAnalytics.identify(userId, email ? { email } : undefined);
}

export function registerAnalyticsProperties(
  properties: Record<string, string | number | boolean | null | undefined>,
): void {
  withAnalytics((analytics) => analytics.register(properties));
}

export function unregisterAnalyticsProperties(
  propertyKeys: readonly string[],
): void {
  withAnalytics((analytics) => {
    for (const propertyKey of propertyKeys) {
      analytics.unregister(propertyKey);
    }
  });
}

export function resetAnalyticsUser(): void {
  deferredAnalytics.reset();
}

export function performTrackedSignOut({
  event,
  properties,
  callbackUrl,
}: {
  readonly event: AnalyticsEventName;
  readonly properties?: Record<
    string,
    string | number | boolean | null | undefined
  >;
  readonly callbackUrl?: string;
}): Promise<void> {
  captureAnalyticsEvent({ event, properties });
  return signOut(callbackUrl ? { callbackUrl } : undefined).then(
    resetAnalyticsUser,
  );
}
